/**
 * LoadingState - Unified loading skeleton component
 * Variant-based skeleton for different page types
 */

interface LoadingStateProps {
  variant?: 'list' | 'cards' | 'kanban' | 'detail' | 'table';
  count?: number;
}

function SkeletonPulse({ className = '' }: { className?: string }) {
  return (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
  );
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-lg border p-4 space-y-3">
      <SkeletonPulse className="h-40 w-full rounded-lg" />
      <SkeletonPulse className="h-4 w-3/4" />
      <SkeletonPulse className="h-4 w-1/2" />
      <div className="flex justify-between items-center pt-2">
        <SkeletonPulse className="h-6 w-20" />
        <SkeletonPulse className="h-8 w-24 rounded-lg" />
      </div>
    </div>
  );
}

function ListItemSkeleton() {
  return (
    <div className="bg-white rounded-lg border p-4 flex items-center gap-4">
      <SkeletonPulse className="h-12 w-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <SkeletonPulse className="h-4 w-1/3" />
        <SkeletonPulse className="h-3 w-1/2" />
      </div>
      <SkeletonPulse className="h-8 w-20 rounded" />
    </div>
  );
}

function KanbanSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto p-4">
      {[1, 2, 3, 4].map((col) => (
        <div key={col} className="min-w-[280px] bg-gray-50 rounded-lg p-3 space-y-3">
          <SkeletonPulse className="h-8 w-full rounded" />
          {[1, 2, 3].map((card) => (
            <div key={card} className="bg-white rounded-lg border p-3 space-y-2">
              <SkeletonPulse className="h-4 w-3/4" />
              <SkeletonPulse className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border p-6 space-y-4">
        <div className="flex items-center gap-4">
          <SkeletonPulse className="h-16 w-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <SkeletonPulse className="h-6 w-1/3" />
            <SkeletonPulse className="h-4 w-1/4" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <SkeletonPulse className="h-3 w-16" />
              <SkeletonPulse className="h-5 w-24" />
            </div>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="bg-white rounded-lg border p-6 space-y-4">
        <SkeletonPulse className="h-5 w-32" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4 py-3 border-b last:border-0">
            <SkeletonPulse className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <SkeletonPulse className="h-4 w-1/2" />
              <SkeletonPulse className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TableSkeleton({ count = 5 }: { count: number }) {
  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 flex gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonPulse key={i} className="h-4 w-24" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="px-4 py-3 flex gap-4 border-t">
          {[1, 2, 3, 4, 5].map((j) => (
            <SkeletonPulse key={j} className="h-4 w-24" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function LoadingState({ variant = 'list', count = 4 }: LoadingStateProps) {
  switch (variant) {
    case 'cards':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
          {Array.from({ length: count }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      );
    case 'kanban':
      return <KanbanSkeleton />;
    case 'detail':
      return <DetailSkeleton />;
    case 'table':
      return <TableSkeleton count={count} />;
    case 'list':
    default:
      return (
        <div className="space-y-3 p-4">
          {Array.from({ length: count }).map((_, i) => (
            <ListItemSkeleton key={i} />
          ))}
        </div>
      );
  }
}
